import { CustomAgentProto } from "build-ts/custom_agent";
import { Header } from "build-ts/metadata";
import { SlackChannel, SlackUser } from "build-ts/slack";
import showToast from "hooks/useCustomToast";

export function postRequest<T>(payload: T): RequestInit {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  };
}

export function getRequest(): RequestInit {
  return {
    method: "GET",
    credentials: "include",
  };
}

export function deleteRequest(): RequestInit {
  return {
    method: "DELETE",
    credentials: "include",
  };
}

/**
 * Create or update a custom agent.
 * 
 * @param {CustomAgentProto} proto - The custom agent to create or update.
 * @returns {Promise<CustomAgentProto | null>} A promise that resolves to the created/updated custom agent data.
 * @throws {Error} Throws an error if there is an issue creating/updating the custom agent or if the request fails.
 */
export const putCustomAgent = async (proto: CustomAgentProto) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_PUT_CUSTOM_AGENT}`,
      postRequest(CustomAgentProto.toJSON(proto))
    );

    if (!response.ok) {
      showToast("Custom Agent Operation Failed", "", "error");
      return null;
    }

    const data = await response.json();
    return CustomAgentProto.fromJSON(data);
  } catch (error) {
    showToast("Custom Agent Operation Failed", "", "error");
    return null;
  }
};

/**
 * Get a custom agent by ID.
 * 
 * @param {string} id - The ID of the custom agent to get.
 * @param {string[]} runIds - Optional array of run IDs.
 * @returns {Promise<CustomAgentProto | null>} A promise that resolves to the custom agent data.
 * @throws {Error} Throws an error if there is an issue getting the custom agent or if the request fails.
 */
export const getCustomAgent = async (id: string, runIds: string[] = []) => {
  try {
    let url = `${process.env.REACT_APP_CUSTOM_AGENT}/${id}`;
    
    if (runIds.length > 0) {
      const params = new URLSearchParams();
      params.append('runIds', JSON.stringify(runIds));
      url += `?${params.toString()}`;
    }
    
    const response = await fetch(url, getRequest());

    if (!response.ok) {
      if (response.status !== 404) {
        showToast("Custom Agent Operation Failed", "", "error");
      }
      return null;
    }

    const data = await response.json();
    return CustomAgentProto.fromJSON(data);
  } catch (error) {
    showToast("Custom Agent Operation Failed", "", "error");
    return null;
  }
};

/**
 * Update agent header
 * 
 * @param {string} agentId - The id of the agent to update.
 * @param {string} name - The name of the agent.
 * @param {string} description - The description of the agent.
 * @returns {Promise<void>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateAgentHeader = async (agentId: string, name: string, description?: string) => {
  const response = await fetch(
    `${process.env.REACT_APP_UPDATE_AGENT_HEADER!}?id=${agentId}&name=${name}&description=${description}`!, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    showToast("Error in updating agent header", "", "error");
    return null;
  }
  return response;
};

/**
 * Delete a custom agent.
 * 
 * @param {string} id - The ID of the custom agent to delete.
 * @returns {Promise<Response | null>} A promise that resolves to the response from the server.
 * @throws {Error} Throws an error if there is an issue deleting the custom agent or if the request fails.
 */
export const deleteCustomAgent = async (id: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CUSTOM_AGENT}/${id}`,
      deleteRequest()
    );

    if (!response.ok) {
      showToast("Failed to delete custom agent", "", "error");
      return null;
    }

    return response;
  } catch (error) {
    showToast("Failed to delete custom agent", "", "error");
    return null;
  }
};

/**
 * List custom agents with optional filtering and pagination.
 * 
 * @param {number} pageSize - The number of agents per page (default: -1)
 * @param {number} pageNumber - The page number to retrieve (default: 1)
 * @param {string} sortByField - The field to sort by (default: "LAST_MODIFIED")
 * @param {boolean} sortAscending - Whether to sort in ascending order (default: false)
 * @returns {Promise<Header[] | null>} A promise that resolves to the list of custom agents.
 * @throws {Error} Throws an error if there is an issue getting the custom agents or if the request fails.
 */
export const listCustomAgents = async (
  pageSize: number = -1,
  pageNumber: number = 1,
  sortByField: string = "LAST_MODIFIED",
  sortAscending: boolean = false,
  includeScheduleActivity: boolean = false,
  activeSchedule: boolean | "",
  searchString: string = ""
) => {
  try {
    const queryParams = new URLSearchParams({
      pageSize: pageSize.toString(),
      pageNumber: pageNumber.toString(),
      sortByField,
      sortAscending: sortAscending.toString(),
      includeScheduleActivity: includeScheduleActivity.toString(),
      activeSchedule: activeSchedule.toString(),
      searchString: searchString
    });

    const response = await fetch(
      `${process.env.REACT_APP_LIST_CUSTOM_AGENT}?${queryParams}`,
      getRequest()
    );

    if (!response.ok) {
      showToast("Failed to get custom agents", "", "error");
      return null;
    }

    const data = await response.json();
    return {headers: data.headers.map((agent: any) => Header.fromJSON(agent)), totalRows: data.total_rows};
  } catch (error) {
    showToast("Failed to get custom agents", "", "error");
    return {headers: [], totalRows: 0};
  }
};
export const cloneCustomAgent = async (agentId: string, save: boolean = false) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CLONE_AGENT}?agentId=${agentId}&save=${save}`,
      getRequest()
    );

    if (!response.ok) {
      showToast("Error in cloning custom agent", "", "error");
      return null;
    }

    const data = await response.json();
    showToast("Custom agent cloned successfully", "", "success");
    return CustomAgentProto.fromJSON(data);
  } catch (error) {
    showToast("Error in cloning custom agent", "", "error");
    return null;
  }
};

/**
 * Get a list of Slack channels.
 * 
 * @returns {Promise<SlackChannel[] | null>} A promise that resolves to the list of Slack channels.
 * @throws {Error} Throws an error if there is an issue getting the Slack channels or if the request fails.
 */
export const getSlackChannels = async (): Promise<SlackChannel[] | null> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_SLACK_CHANNEL_LIST}`,
      getRequest()
    );

    if (!response.ok) {
      showToast("Failed to get Slack channels", "", "error");
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    showToast("Error in getting Slack channels", "", "error");
    return null;
  }
};

/**
 * Get a list of Slack channels users.
 * 
 * @returns {Promise<SlackUser[] | null>} A promise that resolves to the list of Slack channels.
 * @throws {Error} Throws an error if there is an issue getting the Slack channels or if the request fails.
 */
export const getSlackChannelUsers = async (channelId: string | undefined): Promise<SlackUser[] | null> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_SLACK_CHANNEL_LIST}/${channelId}/users`,
      getRequest()
    );

    if (!response.ok) {
      showToast("Failed to get Slack Channel Users", "", "error");
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    showToast("Error in getting Slack Channel Users", "", "error");
    return null;
  }
};
